import { createRouter, createWebHistory } from 'vue-router'
import { loadPoseLibs } from '../services/lazyScripts'

// Pull in the pose-detection stack before a Yoga page mounts, so the views
// still find window.Pose / window.Camera exactly as they did when these
// scripts lived in index.html. Never blocks navigation on failure — the
// views show their own message if the libraries are unavailable.
async function loadPoseBeforeEnter(to, from, next) {
  try { await loadPoseLibs(); } catch (e) { /* views handle the fallback */ }
  next();
}

const routes = [
  {
    path: '/',
    redirect: '/home'
  },
  {
    path: '/home',
    name: 'Home',
    component: () => import('../views/ViewsHome.vue'),
    meta: {}
  },
  {
    path: '/yoga',
    name: 'Yoga',
    component: () => import('../views/YogaPose.vue'),
    // ~1.5 MB of pose-detection libraries, fetched only when Yoga is opened
    beforeEnter: loadPoseBeforeEnter,
    meta: {}
  },
  {
    path: '/yoga-game',
    name: 'YogaGame',
    component: () => import('../views/yoga/YogaGame.vue'),
    beforeEnter: loadPoseBeforeEnter,
    meta: {}
  },
  {
    path: '/singing',
    name: 'SingingHome',
    component: () => import('../views/singing/SingingHome.vue'),
    meta: {}
  },
  {
    path: '/vocal',
    name: 'VocalTraining',
    component: () => import('../views/singing/VocalPage.vue'),
    meta: {}
  },
  {
    path: '/song-practice',
    name: 'SongPractice',
    component: () => import('../views/singing/SongPractice.vue'),
    meta: {}
  },
  {
    path: '/profile',
    name: 'UserProfile',
    component: () => import('../views/UserProfile.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/puzzles',
    name: 'ChessPuzzles',
    component: () => import('../views/chess/ChessPuzzles.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/LoginSignup.vue')
  },
  {
    path: '/multiplayer',
    name: 'Multiplayer',
    component: () => import('../views/chess/MultiplayerChess.vue'),
    meta: {}
  },
  {
    path: '/academics',
    name: 'Academics',
    component: () => import('../views/academics/AcademicsHome.vue'),
    meta: {}
  },
  {
    path: '/academics/english',
    name: 'EnglishLearning',
    component: () => import('../views/academics/EnglishLearning.vue'),
    meta: {}
  },
  {
    path: '/academics/maths',
    name: 'MathsLearning',
    component: () => import('../views/academics/MathsLearning.vue'),
    meta: {}
  },
  {
    path: '/academics/science',
    name: 'ScienceLearning',
    component: () => import('../views/academics/ScienceLearning.vue'),
    meta: {}
  },
  {
    path: '/instruments',
    name: 'Instruments',
    component: () => import('../views/InstrumentsHome.vue'),
    meta: {}
  },
  {
    path: '/arena',
    name: 'QuizArena',
    component: () => import('../views/QuizArena.vue'),
    meta: {}
  },
  {
    path: '/wonder',
    name: 'WonderLab',
    component: () => import('../views/WonderLab.vue'),
    meta: {}
  },
  {
    path: '/math-path',
    name: 'MathPath',
    component: () => import('../views/MathPath.vue'),
    meta: {}
  },
  {
    path: '/sudoku',
    name: 'SudokuGame',
    component: () => import('../views/SudokuGame.vue'),
    meta: {}
  },
  {
    path: '/leaderboard',
    name: 'Leaderboard',
    component: () => import('../views/LeaderboardPage.vue'),
    meta: {}
  },
  {
    // ViewsProgress showed the same Chess/Singing/Yoga cards as the Profile
    // page, and nothing in the app linked to it. Redirect instead of keeping a
    // second, less complete copy of the same screen.
    path: '/progress',
    redirect: '/profile'
  },
  {
    // Catch-all: unknown URLs go home instead of a blank page
    path: '/:pathMatch(.*)*',
    redirect: '/home'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Auth guard — redirect to /login if not authenticated
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem('authToken');
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

  // Guests may browse and learn. Only the account page and chess puzzles
  // (whose every endpoint needs a token) bounce to login — and they come back
  // to where they were once signed in.
  if (requiresAuth && !token) {
    next({ name: 'Login', query: { redirect: to.fullPath } });
  } else {
    // Always allow /login to load: its own token-verify redirects valid
    // sessions to Home, while EXPIRED tokens get cleared there — bouncing
    // token-holders away would trap users with dead tokens.
    next();
  }
})

// Count SPA navigations as pageviews in GoatCounter. The script tag uses
// no_onload, so this hook is the single source of pageviews. count.js
// loads async and may not be ready for the very first navigation —
// retry briefly instead of losing the landing view. No-op if blocked.
router.afterEach((to) => {
  let tries = 0;
  const send = () => {
    if (window.goatcounter && typeof window.goatcounter.count === 'function') {
      window.goatcounter.count({ path: to.path });
    } else if (++tries < 10) {
      setTimeout(send, 500);
    }
  };
  send();
});

export default router